import type { ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import type { TransportReference } from "src/api/sure-api/useSearchTransportReferences";
import { isTransportReferenceApplicableToStage } from "src/api/sure-api/useSearchTransportReferences";
import type { TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import type { IconMappingProps } from "@foodpilot/foods";

export type TransportSummary = {
    modeIcons: Array<{ key: string; icon: IconMappingProps; label: string }>;
    distanceKm: number | null;
};

const EMPTY_TRANSPORT_SUMMARY: TransportSummary = {
    modeIcons: [],
    distanceKm: null,
};

type TransportModeLike = {
    id?: number;
    code?: string | null;
    family?: string | null;
    isRefrigerated?: boolean | null;
    name?: string | null;
    label?: string | null;
};

export type TransportSummaryOption =
    | {
          kind: "reference";
          id: number;
          label: string;
          reference: TransportReference;
      }
    | {
          kind: "circuit";
          id: number;
          label: string;
          circuit: TransportCircuit;
      };

const isLegApplicableToStage = (applicableStages: string | null | undefined, stage: string): boolean => {
    if (applicableStages == null || applicableStages.length === 0) {
        return true;
    }

    return applicableStages.split("|").includes(stage);
};

/**
 * Ecobalyse product distribution uses only the `distribution-product-truck` leg
 * for `storage_to_retail`, not the legacy retail-truck leg on the same circuit.
 */
const isReferenceLegApplicableToStage = (leg: TransportReference["legs"][number], stage: string): boolean => {
    if (!isLegApplicableToStage(leg.applicableStages, stage)) {
        return false;
    }

    if (stage !== "storage_to_retail") {
        return true;
    }

    const legCode = leg.transportLegReference.code ?? "";

    return legCode.startsWith("distribution-product-truck");
};

export const buildTransportOptionSummary = (
    option: TransportSummaryOption,
    stage: string,
    transportModes: TransportModeLike[],
): TransportSummary => {
    if (option.kind === "reference") {
        const legs = [...option.reference.legs]
            .filter((leg) => isReferenceLegApplicableToStage(leg, stage))
            .sort((a, b) => a.orderIndex - b.orderIndex);
        const distanceKm = sumNullable(legs.map((leg) => leg.transportLegReference.distanceKm ?? null));

        return {
            distanceKm,
            modeIcons: uniqueModeIcons(
                legs.map((leg) => {
                    const mode = leg.transportLegReference.transportModeReference;
                    return {
                        key: mode?.id != null ? `reference-mode-${mode.id}` : `reference-mode-${leg.id}`,
                        icon: iconForMode(mode?.family, mode?.code, mode?.isRefrigerated),
                        label: mode?.name ?? mode?.code ?? "Transport mode",
                    };
                }),
            ),
        };
    }

    const legs = [...option.circuit.circuitLegs].sort((a, b) => a.orderIndex - b.orderIndex);
    const resolvedLegs = legs
        .map((leg) => leg.transportLeg)
        .filter((leg): leg is Exclude<typeof leg, string> => typeof leg === "object" && leg !== null);
    const distanceKm = sumNullable(resolvedLegs.map((leg) => leg.distanceKm ?? null));

    return {
        distanceKm,
        modeIcons: uniqueModeIcons(
            resolvedLegs.map((leg) => {
                const mode = transportModes.find((item) => item.id === leg.transportModeId);
                return {
                    key: mode?.id != null ? `circuit-mode-${mode.id}` : `circuit-mode-${leg.id}`,
                    icon: iconForMode(mode?.family, mode?.code, mode?.isRefrigerated),
                    label: mode?.label ?? mode?.name ?? "Transport mode",
                };
            }),
        ),
    };
};

export const buildResolvedTransportSummary = (
    resolvedTransport: ProductLivePreview["resolvedTransports"][number],
): TransportSummary => {
    const legs = resolvedTransport.modeSummary?.legs ?? [];

    return {
        distanceKm: resolvedTransport.distanceKm,
        modeIcons: uniqueModeIcons(
            legs.map((leg, index) => ({
                key: leg.transportModeId != null ? `resolved-mode-${leg.transportModeId}` : `resolved-mode-${index}`,
                icon: iconForMode(null, null, leg.isRefrigerated),
                label: leg.name ?? "Transport mode",
            })),
        ),
    };
};

export const buildSelectedTransportSummary = (
    option: TransportSummaryOption,
    stage: string,
    transportModes: TransportModeLike[],
    resolvedTransport?: ProductLivePreview["resolvedTransports"][number],
): TransportSummary => {
    const localSummary = buildTransportOptionSummary(option, stage, transportModes);

    if (option.kind !== "circuit" || option.circuit.linkMode !== "LINKED") {
        return localSummary;
    }

    const requiresResolvedFallback =
        option.circuit.circuitLegs.length === 0 ||
        option.circuit.circuitLegs.some((leg) => typeof leg.transportLeg === "string");
    if (!requiresResolvedFallback) {
        return localSummary;
    }

    const sourceReferenceCircuitId = option.circuit.sourceReferenceCircuitId;
    if (sourceReferenceCircuitId == null || resolvedTransport?.transportReferenceId !== sourceReferenceCircuitId) {
        return EMPTY_TRANSPORT_SUMMARY;
    }

    return buildResolvedTransportSummary(resolvedTransport);
};

type BuildProductTransportSummaryParams = {
    hasPersistedSelection: boolean;
    selectedOption: TransportSummaryOption | null;
    effectiveSelectedOption: TransportSummaryOption | null;
    resolvedTransport?: ProductLivePreview["resolvedTransports"][number];
    stage: string;
    transportModes: TransportModeLike[];
};

export const buildProductTransportSummary = ({
    hasPersistedSelection,
    selectedOption,
    effectiveSelectedOption,
    resolvedTransport,
    stage,
    transportModes,
}: BuildProductTransportSummaryParams): TransportSummary => {
    if (selectedOption !== null) {
        return buildSelectedTransportSummary(selectedOption, stage, transportModes, resolvedTransport);
    }

    if (hasPersistedSelection) {
        return EMPTY_TRANSPORT_SUMMARY;
    }

    if (resolvedTransport !== undefined) {
        return buildResolvedTransportSummary(resolvedTransport);
    }

    if (effectiveSelectedOption !== null) {
        return buildTransportOptionSummary(effectiveSelectedOption, stage, transportModes);
    }

    return EMPTY_TRANSPORT_SUMMARY;
};

export const isReferenceOptionApplicableToStage = (reference: TransportReference, stage: string): boolean =>
    isTransportReferenceApplicableToStage(reference, stage);

const uniqueModeIcons = (modeIcons: TransportSummary["modeIcons"]): TransportSummary["modeIcons"] => {
    const seen = new Set<string>();

    return modeIcons.filter((modeIcon) => {
        if (seen.has(modeIcon.key)) {
            return false;
        }

        seen.add(modeIcon.key);
        return true;
    });
};

const iconForMode = (
    family: string | null | undefined,
    code: string | null | undefined,
    isRefrigerated: boolean | null | undefined,
): IconMappingProps => {
    const normalizedCode = code?.toLowerCase() ?? "";
    if (normalizedCode.includes("train") || normalizedCode.includes("rail")) {
        return "train";
    }

    switch (family?.toLowerCase()) {
        case "sea":
            return isRefrigerated ? "refrigeratedBoat" : "boat";
        case "air":
            return "transportation";
        case "land":
        default:
            return isRefrigerated ? "refrigeratedTransport" : "transportation";
    }
};

const sumNullable = (values: Array<number | null>): number | null => {
    if (values.length === 0 || !values.every((value): value is number => value !== null)) {
        return null;
    }

    return values.reduce((total, value) => total + value, 0);
};
